import { bind } from 'bind-event-listener';

import type { getPosHandlerNode } from '@atlaskit/editor-common/types';
import type { Node as PmNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

const NO_SELECT_ATTR_SELECTOR = '[data-no-extension-select]';
export const AGENT_MANAGED_EXTENSION_CLASSNAME = 'agent-managed-extension';
export const BODIED_EXTENSION_CONTENT_DOM_WRAPPER_CLASSNAME =
	'bodiedExtension-content-dom-wrapper';
export const AGENT_MANAGED_EXTENSION_CONTENT_DOM_ATTR =
	'data-agent-managed-extension-content-dom';

export interface NodeViewDom {
	/** Editable body container exposed to ProseMirror for editable node views. */
	contentDOM?: HTMLDivElement;
	/** The element React content is portalled into. Carries the
	 * canonical `extension-container` class so floating-toolbar
	 * anchoring (`findDomRefAtPos`) resolves to it. */
	mountPoint: HTMLDivElement;
	/** The outer wrapper appended to the editor by ProseMirror. */
	root: HTMLDivElement;
}

/**
 * Builds the static DOM scaffold for the agent-managed-extension
 * nodeView. Pure — only touches the supplied `doc`.
 */
export const createNodeViewDom = (
	doc: Document,
	node: PmNode,
	options: { includeContentDOM?: boolean } = {},
): NodeViewDom => {
	const root = doc.createElement('div');
	root.classList.add(AGENT_MANAGED_EXTENSION_CLASSNAME);
	root.setAttribute('data-node-type', 'bodied-extension');
	root.setAttribute('data-extension-type', node.attrs.extensionType);
	root.setAttribute('data-extension-key', node.attrs.extensionKey);
	root.setAttribute('data-local-id', node.attrs.localId ?? '');

	const mountPoint = doc.createElement('div');
	mountPoint.classList.add('extension-container');
	root.appendChild(mountPoint);

	if (!options.includeContentDOM) {
		return { root, mountPoint };
	}

	const contentDOM = doc.createElement('div');
	contentDOM.classList.add(BODIED_EXTENSION_CONTENT_DOM_WRAPPER_CLASSNAME);
	contentDOM.setAttribute(AGENT_MANAGED_EXTENSION_CONTENT_DOM_ATTR, 'true');
	root.appendChild(contentDOM);

	return { root, mountPoint, contentDOM };
};

/**
 * Wires a `mousedown` handler on `dom` that turns body clicks into a
 * `NodeSelection` of the bodied extension at `getPos()`. Returns a
 * cleanup function. Clicks on `[data-no-extension-select]` descendants
 * (e.g. popup buttons) and editable body content are ignored so chrome
 * controls and text editing keep working.
 */
export const attachBodyClickSelection = (
	view: EditorView,
	dom: HTMLElement,
	getPos: getPosHandlerNode,
): (() => void) => {
	const handle = (e: MouseEvent) => {
		const pos = getPos();
		if (typeof pos !== 'number') {
			return;
		}
		const target = e.target as HTMLElement | null;
		if (
			target?.closest(
				`${NO_SELECT_ATTR_SELECTOR}, [${AGENT_MANAGED_EXTENSION_CONTENT_DOM_ATTR}]`,
			)
		) {
			return;
		}
		e.preventDefault();
		view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));
	};
	return bind(dom, { type: 'mousedown', listener: handle });
};
